import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { ExpiryPicker } from "@/components/ExpiryPicker";

const EMPTY = { code: "", kind: "percent", value: "", plans: ["month", "year"], expires_at: "", max_uses: "" };
const pill = (on) => `whitespace-nowrap shrink-0 px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-amber-500 text-onaccent border-amber-500" : "border-line text-slate-400 hover:text-amber-200"}`;

export const PromoForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const togglePlan = (p) => setF({ ...f, plans: f.plans.includes(p) ? f.plans.filter((x) => x !== p) : [...f.plans, p] });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.plans.length) return toast.error("Pick at least one plan");
    setBusy(true);
    try {
      const body = { ...f, value: Number(f.value), max_uses: f.max_uses ? Number(f.max_uses) : null, expires_at: f.expires_at || null };
      onCreated((await api.post("/admin/promo-codes", body)).data);
      setF(EMPTY);
      toast.success("Promo code created");
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-line bg-panel p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="promo-create-form">
      <div className="space-y-1.5"><Label>Code name</Label>
        <Input required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "") })} placeholder="NANOWRIMO25" className="font-code tracking-widest" data-testid="promo-form-code" /></div>
      <div className="space-y-1.5"><Label>Discount</Label>
        <div className="flex gap-2">
          <button type="button" className={pill(f.kind === "percent")} onClick={() => setF({ ...f, kind: "percent" })} data-testid="promo-form-kind-percent">% off</button>
          <button type="button" className={pill(f.kind === "amount")} onClick={() => setF({ ...f, kind: "amount" })} data-testid="promo-form-kind-amount">$ off</button>
          <Input required type="number" min="0.01" step="0.01" value={f.value} onChange={set("value")} placeholder={f.kind === "percent" ? "20" : "5.00"} className="h-9" data-testid="promo-form-value" />
        </div></div>
      <div className="space-y-1.5"><Label>Works on</Label>
        <div className="flex gap-2">
          <button type="button" className={pill(f.plans.includes("month"))} onClick={() => togglePlan("month")} data-testid="promo-form-plan-month">Monthly</button>
          <button type="button" className={pill(f.plans.includes("year"))} onClick={() => togglePlan("year")} data-testid="promo-form-plan-year">Yearly</button>
        </div></div>
      <div className="space-y-1.5"><Label>Expires <span className="text-slate-500">(optional)</span></Label>
        <ExpiryPicker value={f.expires_at} onChange={(v) => setF({ ...f, expires_at: v })} /></div>
      <div className="space-y-1.5"><Label>Usage limit <span className="text-slate-500">(optional)</span></Label>
        <Input type="number" min="1" step="1" value={f.max_uses} onChange={set("max_uses")} placeholder="Unlimited" data-testid="promo-form-max-uses" /></div>
      <div className="flex items-end">
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold" data-testid="promo-form-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create code
        </Button>
      </div>
    </form>
  );
};
